import axios from 'axios';
import {
  Agent,
  AgentDetail,
  AgentListResponse,
  ChatHistoryResponse,
  ChatResponse,
  CreateAgentRequest,
  CreateAgentResponse,
  DeleteAgentResponse,
  EndConditionCheckResponse,
} from '../types';

// Create axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://cloudflare-ai-agent-backend.xavier-91f.workers.dev/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// Log the API URL for debugging
console.log('API URL:', process.env.REACT_APP_API_URL || 'https://cloudflare-ai-agent-backend.xavier-91f.workers.dev/api');

// We don't need client-side CORS headers since they're handled by the backend

// Add a response interceptor for better error logging
api.interceptors.response.use(function (response) {
  // Any status code that lie within the range of 2xx cause this function to trigger
  return response;
}, function (error) {
  // Any status codes that falls outside the range of 2xx cause this function to trigger
  console.error('API Response Error:', error);
  
  if (error.code === 'ERR_NETWORK') {
    console.error('Network error - backend server might be down or unreachable');
  } else if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error('Server responded with error:', error.response.status, error.response.data);
  } else if (error.request) {
    // The request was made but no response was received
    console.error('No response received from server');
  }
  
  return Promise.reject(error);
});

// Agent API
export const agentApi = {
  // Get all agents
  getAgents: async (): Promise<Agent[]> => {
    const response = await api.get<AgentListResponse>('/agents');
    return response.data.agents;
  },

  // Get agent details
  getAgentDetails: async (agentId: string): Promise<AgentDetail> => {
    console.log(`Fetching details for agent: ${agentId}`);
    const response = await api.get<AgentDetail>(`/agents/${agentId}`);
    console.log('Agent details response:', JSON.stringify(response.data, null, 2));
    return response.data;
  },

  // Create agent
  createAgent: async (data: CreateAgentRequest): Promise<CreateAgentResponse> => {
    console.log('Creating agent with data:', JSON.stringify(data, null, 2));
    const response = await api.post<CreateAgentResponse>('/agents', data);
    console.log('Create agent response:', response.data);
    return response.data;
  },

  // Delete agent
  deleteAgent: async (agentId: string): Promise<DeleteAgentResponse> => {
    const response = await api.delete<DeleteAgentResponse>(`/agents/${agentId}`);
    return response.data;
  },
};

// Chat API
export const chatApi = {
  // Send message to agent
  sendMessage: async (agentId: string, message: string): Promise<ChatResponse> => {
    const response = await api.post<ChatResponse>(`/agents/${agentId}/chat`, { message });
    return response.data;
  },

  // Get chat history
  getChatHistory: async (agentId: string): Promise<ChatHistoryResponse> => {
    const response = await api.get<ChatHistoryResponse>(`/agents/${agentId}/history`);
    return response.data;
  },

  // Check end conditions
  checkEndConditions: async (agentId: string): Promise<EndConditionCheckResponse> => {
    console.log(`Checking end conditions for agent: ${agentId}`);
    const response = await api.get<EndConditionCheckResponse>(`/agents/${agentId}/check-conditions`);
    console.log('End conditions check response:', JSON.stringify(response.data, null, 2));
    return response.data;
  },
};

export default api;
